import ContactInfoForm, {
  Contact_INFO_FORM_VIEW_TYPES,
} from "../contact-info-form";
import { useForm } from "react-hook-form";
import fuzzysort from "fuzzysort";
import { Typography } from "@mui/material";
import { Navigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import React, { useState, useEffect } from "react";
import { contactsCollection } from "../../../services/firebase";
import { getNextContactId } from "../../../services/countersService";
import { CreateContactModalStyled } from "./create-contact-modal.style";
import { yupResolver } from "@hookform/resolvers/yup";
import { CreateContactValidations } from "../../../constants/validations";

const CreateContactModal = (props) => {
  const {
    onClose,
    nextId,
    defaultValues = {},
    defaultCompanyID,
  } = props;
  const { userDetails } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [redirectPath, setRedirectPath] = useState(null);

  const form = useForm({
    mode: "all",
    defaultValues: {
      id: nextId?.toString(),
      firstName: "",
      lastName: "",
      title: "",
      companyID: defaultCompanyID || "",
      type: "",
      contactState: "",
      webAddress: "",
      phoneNumber: "",
      contactNotes: "",
      ...defaultValues,
    },
    resolver: yupResolver(CreateContactValidations()),
  });

  const { formState, getValues } = form;
  const { isValid } = formState;

  useEffect(() => {
    form.reset({
      ...form.getValues(),
      id: nextId ? nextId.toString() : "",
    });
  }, [nextId, form]);

  const handleSaveModal = async (formData) => {
    console.log("Received form data:", formData);
    setIsLoading(true);
  
    try {
      // Step 1: Fetch all existing contacts from Firestore
      const snapshot = await contactsCollection.get();
      const existingContacts = snapshot.docs.map((doc) => ({
        firstName: doc.data().firstName.trim().toLowerCase(), // Trim spaces
        lastName: doc.data().lastName.trim().toLowerCase(),   // Trim spaces
        companyID: doc.data().companyID,
      }));
  
      const enteredFirstName = formData.firstName.trim().toLowerCase(); // Trim user input
      const enteredLastName = formData.lastName.trim().toLowerCase();
      const enteredCompanyID = formData.companyID;
  
      // Step 2: Check for exact match (same name AND same company)
      const exactMatch = existingContacts.some(
        (contact) =>
          contact.firstName === enteredFirstName &&
          contact.lastName === enteredLastName &&
          contact.companyID === enteredCompanyID
      );
  
      if (exactMatch) {
        alert("Unable to create contact. Another contact with the same name and company already exists.");
        setIsLoading(false);
        return;
      }
  
      // Step 3: Check for similar name matches across all companies
      const existingNames = existingContacts.map(
        (contact) => `${contact.firstName} ${contact.lastName}`
      );
  
      const enteredFullName = `${enteredFirstName} ${enteredLastName}`;
  
      const similarMatches = fuzzysort.go(enteredFullName, existingNames, { threshold: -100 });
  
      if (similarMatches.length > 0) {
        const confirmed = window.confirm(
          "Are you sure? Another contact with the same or similar name already exists. Press OK to proceed, or Cancel to go back."
        );
        if (!confirmed) {
          setIsLoading(false);
          return;
        }
      }
  
      // Step 4: Proceed with contact creation
      const currentDate = new Date();
      const formattedDate = `${currentDate.getDate()}/${currentDate.getMonth() + 1}/${currentDate.getFullYear()}`;
      formData.createdBy = userDetails?.fullName;
      formData.dateCreated = formattedDate;
  
      const contactId = await getNextContactId();
      const contactData = {
        id: contactId,
        firstName: formData.firstName.trim(),  // Trim before saving
        lastName: formData.lastName.trim(),
        type: formData.type,
        contactState: formData.contactState,
        title: formData.title,
        emailAddress: formData.emailAddress,
        phoneNumber: formData.phoneNumber,
        companyID: formData.companyID,
        contactNotes: formData.contactNotes,
        createdBy: formData.createdBy,
        dateCreated: formattedDate,
      };
  
      const contactRef = contactsCollection.doc(contactId.toString());
      await contactRef.set(contactData);
  
      console.log("Contact saved successfully:", contactData);
      onSuccess(contactData);
    } catch (error) {
      console.error("Error saving contact:", error);
    } finally {
      setIsLoading(false);
    }
  };
  
  

  const handleSubmit = () => {
    const formData = getValues();
    handleSaveModal(formData);
  };

  const onSuccess = (contactData) => {
    setRedirectPath(`/contactsview/${contactData.id}/details`);
    onClose(); // Close modal
  };

  if (redirectPath) {
    return <Navigate to={redirectPath} />;
  }

  return (
    <CreateContactModalStyled
      confirmBtnTitle="Create Contact"
      confirmBtnColor="#77E250"
      closeBtnTitle="Cancel"
      isConfirming={isLoading}
      disableConfirmBtn={!isValid}
      onConfirm={handleSubmit}
      onClose={onClose}
    >
      <div>
        <div className="head">
          <Typography variant="h4">Create Contact</Typography>
        </div>
        <hr className="hr"></hr>
        <ContactInfoForm
          form={form}
          view={Contact_INFO_FORM_VIEW_TYPES.creation}
          // disableCompanyName={isSomething(defaultCompanyID)}
        />
      </div>
    </CreateContactModalStyled>
  );
};

export default CreateContactModal;
